import React from "react";
import { Transition } from "react-transition-group";
import styled from "styled-components";

import { Tooltips } from ".";
import userBoxes from "../assets/svg/userBoxes.svg";
import userClimb from "../assets/svg/userClimbing.svg";
import userCog from "../assets/svg/userCog.svg";
import userSignal from "../assets/svg/userSignal.svg";
import userStand from "../assets/svg/userStand.svg";

type DataType = {
  imgSrc: string;
  title: string;
  description: JSX.Element[];
};

const DATA: DataType[] = [
  {
    imgSrc: userStand,
    title: "Pre-contemplation",
    description: [
      <small className="d-block mb-0">
        The employee is not aware that their behaviour is unhealthy.
      </small>,
      <ul className="bullet-fulcrum bullet-gold mb-4">
        <li>
          <small className="d-block mb-0">Raising awareness</small>
        </li>
        <li>
          <small className="d-block mb-0">Self-monitoring exercises</small>
        </li>
        <li>
          <small className="d-block mb-0">Healthy policies</small>
        </li>
      </ul>,
      <ul className="bullet-fulcrum bullet-gold mb-4">
        <li>
          <small className="d-block mb-0">Put up signs near the elevator encouraging staff to take the stairs</small>
        </li>
        <li>
          <small className="d-block mb-0">Offer staff pedometers to count daily steps</small>
        </li>
        <li>
          <small className="d-block mb-0">Active transport policy</small>
        </li>
      </ul>,
    ],
  },
  {
    imgSrc: userSignal,
    title: "Contemplation",
    description: [
      <small className="d-block mb-0">
        The employee is weighing up the pros and cons of changing their
        behaviour.
      </small>,
      <ul className="bullet-fulcrum bullet-gold mb-4">
        <li>
          <small className="d-block mb-0">All of the previous</small>
        </li>
        <li>
          <small className="d-block mb-0">
            Healthy environments and infrastructure
          </small>
        </li>
      </ul>,
      <ul className="bullet-fulcrum bullet-gold mb-4">
        <li>
          <small className="d-block mb-0">End-of-trip facilities</small>
        </li>
        <li>
          <small className="d-block mb-0">Sit-stand workstations</small>
        </li>
      </ul>,
    ],
  },
  {
    imgSrc: userBoxes,
    title: "Preparation",
    description: [
      <small className="d-block mb-0">
        The employee is planning on changing their unhealthy behaviour.
      </small>,
      <ul className="bullet-fulcrum bullet-gold mb-4">
        <li>
          <small className="d-block mb-0">All of the previous</small>
        </li>
        <li>
          <small className="d-block mb-0">Providing resources and skill building opportunities</small>
        </li>
      </ul>,
      <ul className="bullet-fulcrum bullet-gold mb-4">
        <li>
          <small className="d-block mb-0">Education sessions on how to increase physical activity during the work day</small>
        </li>
        <li>
          <small className="d-block mb-0">
            <a href="https://livelighter.com.au/order/resources" target="_blank" rel="noopener noreferrer" className="text-reset text-underline">
              Provide free recipe booklets
            </a>
          </small>
        </li>
      </ul>,
    ],
  },
  {
    imgSrc: userClimb,
    title: "Action",
    description: [
      <small className="d-block mb-0">
        The employee is in the process of changing their unhealthy behaviour.
      </small>,
      <ul className="bullet-fulcrum bullet-gold mb-4">
        <li>
          <small className="d-block mb-0">All of the previous</small>
        </li>
        <li>
          <small className="d-block mb-0">
            Providing opportunities to participate
          </small>
        </li>
      </ul>,
      <ul className="bullet-fulcrum bullet-gold mb-4">
        <li>
          <small className="d-block mb-0">Lunch time walking group</small>
        </li>
        <li>
          <small className="d-block mb-0">Standing or walking meetings</small>
        </li>
      </ul>,
    ],
  },
  {
    imgSrc: userCog,
    title: "Maintenance",
    description: [
      <small className="d-block mb-0">
        The employee has adopted the new healthy behaviour and ceased the
        unhealthy one.
      </small>,
      <ul className="bullet-fulcrum bullet-gold mb-4">
        <li>
          <small className="d-block mb-0">All of the previous</small>
        </li>
        <li>
          <small className="d-block mb-0">Supportive workplace culture</small>
        </li>
      </ul>,
      <ul className="bullet-fulcrum bullet-gold mb-4">
        <li>
          <small className="d-block mb-0">
            Afternoon lawn bowls (instead of drinks)
          </small>
        </li>
      </ul>,
    ],
  },
];

export const StagesOfChange: React.FC<{}> = () => {
  const [selection, setSelection] = React.useState<number>(0);
  const [change, setChange] = React.useState<boolean>(true);

  const nodeRef = React.useRef(null);

  const selectionChange = (id: number) => {
    if (id !== selection) {
      setChange(false);
      setTimeout(() => {
        setSelection(id);
        setChange(true);
      }, 800);
    }
  };

  return (
    <>
      <h2 className="h1 text-uppercase text-center mb-4">The stages of change</h2>
      <p className="d-block w-75 text-center mx-auto mb-4">
        The following demonstrates the five successive stages of behaviour change
        <Tooltips id={26} />{" "}
        and the workplace strategies that can be used to help individuals work
        towards the maintenance of a healthy lifestyle
      </p>
      <div className="w-50 position-relative d-flex mx-auto mb-4">
        <Pill active={selection === 0} onClick={() => selectionChange(0)}>
          Stages
        </Pill>
        <Pill active={selection === 1} onClick={() => selectionChange(1)}>
          Helpful Strategies
        </Pill>
        <Pill active={selection === 2} onClick={() => selectionChange(2)}>
          Practical Examples
        </Pill>
      </div>
      <p className="d-block w-75 text-center mx-auto">For more ideas of strategies you could implement as part of your workplace health program, download our best practice guide.</p>
      <div className="row mb-4" style={{ minHeight: 750 }}>
        <div className="col-1"></div>
        {DATA.map(({ imgSrc, title, description }, idx) => (
          <Transition
            nodeRef={nodeRef}
            key={idx}
            timeout={500}
            in={change}
            out={!change}
            appear
          >
            {(status) => (
              <div
                key={idx}
                className="col-2 px-0 d-flex flex-column justify-content-end"
              >
                <img
                  className="img-fluid align-self-center"
                  src={imgSrc}
                  alt={`${title} icon`}
                  width={130}
                  height={130}
                />
                <StyledStep
                  step={idx + 1}
                  className={`transition transition-${status}`}
                >
                  <h3 className="h4">{title}</h3>
                  <span>{description[selection]}</span>
                </StyledStep>
              </div>
            )}
          </Transition>
        ))}
      </div>
    </>
  );
};

const Pill = styled.button<{ active: boolean }>`
  color: white;
  padding: 0.5rem;
  border: none;
  width: 33%;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 0.8rem;
  background: ${(p) => (p.active ? p.theme.warning : p.theme.primary)};
  &:focus {
    outline: none;
  }
  &:first-of-type {
    border-right: 1px solid lightblue;
    border-radius: 2rem 0 0 2rem;
  }
  &:last-of-type {
    border-left: 1px solid lightblue;
    border-radius: 0 2rem 2rem 0;
  }
`;

const StyledStep = styled.div<{ step: number }>`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.primary}99;

  &.transition {
    transition: all 300ms ${(p) => p.step * 100}ms;
    height: 200px;
    span {
      transition: opacity 300ms;
      opacity: 0;
    }
  }
  &.transition-entering,
  &.transition-entered {
    height: ${(p) => p.step * 100 + 100}px;
    span {
      opacity: 1;
    }
  }
  &.transition-exiting,
  &.transition-exited {
    height: 200px;
    span {
      opacity: 0;
    }
  }
`;
